import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, PhoneIcon, TruckIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { CTABand } from '../components/CTABand';
import { SITE } from '../lib/site';
const TIERS = [{
  name: 'Single Item',
  price: '$89',
  unit: 'starting',
  fill: 'A couch, mattress, or appliance',
  features: ['One large item', 'We do the lifting', 'Same-day available', 'Responsibly disposed'],
  featured: false
}, {
  name: 'Quarter Trailer',
  price: '$179',
  unit: 'starting',
  fill: 'A room\'s worth of junk',
  features: ['Up to ¼ trailer load', 'Furniture & boxes', 'Full cleanup included', 'Donate & recycle sorting'],
  featured: true
}, {
  name: 'Full Trailer',
  price: '$549',
  unit: 'starting',
  fill: 'A whole garage or cleanout',
  features: ['Full trailer load', 'Multi-room cleanouts', 'Crew of 2+', 'Sweep-up included'],
  featured: false
}];
export function Pricing() {
  return <>
      <PageHero eyebrow="Pricing" title={<>Honest, <span className="text-jk-red" data-id="element-256" data-mp="14oadqt6ezh6x">upfront pricing</span></>} subtitle="You pay for the space your junk takes up in the trailer — nothing more. You'll always get the exact price before we lift a finger." data-id="element-255" data-mp="1jmmaeztsepy3" />

      <section className="bg-jk-ink py-20" data-id="element-257" data-mp="1xdhfe1160mwe">
        <div className="mx-auto max-w-7xl px-4 sm:px-6" data-id="element-258" data-mp="1xesh7vb1vkkv">
          <div className="grid gap-6 md:grid-cols-3" data-id="element-259" data-mp="22a7scfhob2dl">
            {TIERS.map((tier, i) => <motion.div key={tier.name} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`relative flex flex-col rounded-2xl border p-7 ${tier.featured ? 'border-jk-red bg-jk-panel ring-2 ring-jk-red/40' : 'border-white/10 bg-jk-panel'}`} data-id="element-260" data-mp="1nllao74kpyj7" data-mpi={i}>
                {tier.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-jk-red px-4 py-1 font-display text-sm font-bold uppercase tracking-wide text-white" data-id="element-261" data-mp="2pzfoukt14xyb">
                    Most Popular
                  </span>}
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-jk-red/15 text-jk-red" data-id="element-262" data-mp="2byv9xzq8jma3">
                  <TruckIcon className="h-6 w-6" data-id="element-263" data-mp="277dhy166ww19" />
                </div>
                <h3 className="mt-4 font-display text-3xl font-extrabold uppercase text-white" data-id="element-264" data-mp="229265kwc4bge">{tier.name}</h3>
                <p className="text-sm text-white/50" data-id="element-265" data-mp="1y89vcn7tdyi0">{tier.fill}</p>
                <div className="mt-4 flex items-end gap-2" data-id="element-266" data-mp="1y5x75sbgqrwi">
                  <span className="font-display text-5xl font-extrabold text-jk-red" data-id="element-267" data-mp="1t7c7bu04ia8l">{tier.price}</span>
                  <span className="mb-1.5 text-sm uppercase tracking-wide text-white/50" data-id="element-268" data-mp="1oa2jujwao8ne">{tier.unit}</span>
                </div>
                <ul className="mt-6 flex-1 space-y-3" data-id="element-269" data-mp="3tw1v1hbd3ia">
                  {tier.features.map(f => <li key={f} className="flex items-center gap-2 text-white/80" data-id="element-270" data-mp="8sfqj9naktec">
                      <CheckIcon className="h-5 w-5 flex-shrink-0 text-jk-red" data-id="element-271" data-mp="dnla41t81vz9" /> {f}
                    </li>)}
                </ul>
                <a href={SITE.phoneHref} className={`mt-7 flex items-center justify-center gap-2 rounded-lg px-5 py-3.5 font-display text-xl font-bold uppercase tracking-wide transition-colors ${tier.featured ? 'bg-jk-red text-white hover:bg-jk-redDark' : 'border-2 border-white/20 text-white hover:bg-white/5'}`} data-id="element-272" data-mp="3h77hyg3rvhuo">
                  <PhoneIcon className="h-5 w-5" data-id="element-273" data-mp="2yeviq28f0wor" /> Book This
                </a>
              </motion.div>)}
          </div>

          <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-white/50" data-id="element-274" data-mp="32fp34lmnftcw">
            Every job is different — heavy materials, stairs, and distance can affect the price. That's exactly why we
            give a <span className="text-white" data-id="element-275" data-mp="2ebzbd4rzy4ax">free, no-obligation estimate</span> before we start.
          </p>
        </div>
      </section>

      <section className="bg-white py-20" data-id="element-276" data-mp="2obgepfjs063q">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6" data-id="element-277" data-mp="2oaout1zjjwta">
          <SectionHeading light eyebrow="Not Sure What You Need?" title={<>Get a <span className="text-jk-red" data-id="element-279" data-mp="2x3a31o5jql3v">free estimate</span></>} subtitle="Snap a photo or just describe the pile. We'll give you a straight answer, fast." data-id="element-278" data-mp="322d6x59sila9" />
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-lg bg-jk-red px-8 py-4 font-display text-2xl font-bold uppercase tracking-wide text-white transition-transform hover:scale-[1.03] hover:bg-jk-redDark" data-id="element-280" data-mp="3727x16lqr6u9">
            Request My Quote
          </Link>
        </div>
      </section>

      <CTABand data-id="element-281" data-mp="3ocli9qomma9x" />
    </>;
}